import { fetchCompanies } from 'api/Companies/actions'
import type { CountryResponse } from 'api/Countries/types'
import { Loader, Table } from 'components'
import { selectCompanies, selectRequestParams } from 'data/selectors'
import moment from 'locale/momentLocale'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { CompanyDelayed, Date, Hour } from './CellComponents'
import { COUNTRIES_LIST_TYPES } from './constants'

interface Props {
  countries: CountryResponse[]
  isFetching: boolean
  onRowClick: (row: CountryResponse) => void
  tableType?: string
}

const noReportsDefault = [
  { countryId: 'reno', countryName: 'HR Group - Reno' },
  { countryId: 'germany', countryName: 'CCC Germany' },
]

function CountriesTable({ countries, isFetching, onRowClick, tableType }: Props) {
  const { t: translate } = useTranslation()
  const dispatch = useDispatch()
  const requestParams = useSelector(selectRequestParams)
  const { data: companies, isLoading } = useSelector(selectCompanies)
  let data = []
  let columns = []

  useEffect(() => {
    if (tableType === COUNTRIES_LIST_TYPES.LATEST_REPORTS) {
      dispatch(fetchCompanies(requestParams))
    }
  }, [dispatch, requestParams, tableType])

  if (isFetching || isLoading) return <Loader />

  if (tableType === COUNTRIES_LIST_TYPES.LATEST_REPORTS) {
    data = companies
      .filter(({ timestamp }) => timestamp)
      .sort((a, b) => moment(b.timestamp).diff(a.timestamp, 'seconds') || a.companyName.toString().localeCompare(b.companyName))

    columns = [
      {
        key: 'company',
        title: translate('revenue.stats.completeness.company'),
        CellRender: CompanyDelayed,
      },
      {
        key: 'date',
        title: translate('common.date'),
        CellRender: Date,
      },
      {
        key: 'hour',
        title: translate('revenue.stats.completeness.hour'),
        CellRender: Hour,
      },
    ]
  } else if (tableType === COUNTRIES_LIST_TYPES.NO_REPORTS) {
    data = noReportsDefault
      .concat(
        countries
          .filter(({ timestamp }) => !timestamp)
          .map(country => ({
            countryId: country.countryId,
            countryName: country.countryNamePL,
          })),
      )
      .sort((a, b) => a.countryName.toString().localeCompare(b.countryName))

    columns = [
      {
        key: 'company',
        title: translate('revenue.stats.completeness.noReports'),
        CellRender: CompanyDelayed,
      },
    ]
  } else {
    return null
  }

  if (!data.length) return null

  return <Table columns={columns} data={data} onRowClick={onRowClick} tableClassName="completeness-details-table" />
}

export default CountriesTable
